// src/components/ModerationPanel.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Paper,
    Typography,
    Link,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Alert,
    Grid,
    Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { PubObject } from './FindPubs';
import {
    getUnmoderatedPubs,
    getPlaceDetails,
    fetchMinimalMeshes,
    approveGarden,
    deleteGarden,
    submitGardenToDatabase,
    updateGardenUncertainty,
    latLonElevToECEF,
    gardenCallbackECEFToLatLonElev
} from '../API';
import GardenDrawController from './garden_draw/GardenDrawController';
import TopNav from './TopNav';
import Fonts from './Fonts';
import { Footer } from './Footer';
import { themeColors } from '../styles/theme';
import { Garden } from '../types/gardenTypes';
import axios from 'axios';

// Icons
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

interface ModerationQueueItem {
    google_place_id: string;
    date_added: string;
    sub_areas: Array<{ uid: string; name: string; }>;
    first_garden_submitter_name: string;
    first_garden_submitter_type: 'anonymous' | 'registered' | 'unknown' | 'anonymous (no profile link)';
}

interface PubDataForModeration extends PubObject {
    original_sub_areas_count?: number;
    first_submitter_name_from_queue?: string;
    first_submitter_type_from_queue?: string;
    date_submitted?: string;
}

const ModerationPanel: React.FC = () => {
    const { user, profile, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    const [unmoderatedPubs, setUnmoderatedPubs] = useState<ModerationQueueItem[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [currentPubData, setCurrentPubData] = useState<PubDataForModeration | null>(null);

    const [queueLoading, setQueueLoading] = useState<boolean>(true);
    const [pubDetailsLoading, setPubDetailsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [actionMessage, setActionMessage] = useState<string | null>(null);
    const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

    const [meshUris, setMeshUris] = useState<string[]>([]);
    const [isFetchingMeshes, setIsFetchingMeshes] = useState<boolean>(false);
    const [showDrawingTools, setShowDrawingTools] = useState<boolean>(false);
    const [pendingGardenModification, setPendingGardenModification] = useState<{
        coordinates: [number, number, number][];
        gardenName?: string;
        avgElevation: number;
        isUncertain?: boolean;
        existingGardenUid?: string;
    } | null>(null);
    const [reloadGardenController, setReloadGardenController] = useState<number>(0);

    const [confirmRejectDialogOpen, setConfirmRejectDialogOpen] = useState<boolean>(false);
    const currentlyLoadingPubIdRef = useRef<string | null>(null);

    // Check authorization and fetch unmoderated pubs
    useEffect(() => {
        if (authLoading) return;

        if (!user) {
            setIsAuthorized(false);
            setError("Access Denied: You must be logged in as staff.");
            setQueueLoading(false);
            return;
        }

        setQueueLoading(true);
        setError(null);

        const fetchPubsAndCheckAuth = async () => {
            try {
                const response = await getUnmoderatedPubs(1, 10);
                setIsAuthorized(true);

                if (response.data?.pubs) {
                    setUnmoderatedPubs(response.data.pubs);
                    if (response.data.pubs.length > 0) {
                        setCurrentIndex(0);
                    } else {
                        setActionMessage("No pubs currently awaiting moderation.");
                    }
                } else {
                    setUnmoderatedPubs([]);
                    setActionMessage("No pubs currently awaiting moderation.");
                }
            } catch (err: any) {
                if (axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 401)) {
                    setIsAuthorized(false);
                    setError(err.response?.status === 403 ? "Access Denied: You do not have permission." : "Authentication Error: Please log in as staff.");
                } else {
                    setIsAuthorized(null);
                    setError("Failed to load moderation queue.");
                }
                setUnmoderatedPubs([]);
            } finally {
                setQueueLoading(false);
            }
        };

        fetchPubsAndCheckAuth();
    }, [authLoading, user]);

    // Load pub details when index changes
    const loadPubDetails = useCallback(async (item: ModerationQueueItem | null) => {
        if (!item?.google_place_id) {
            setCurrentPubData(null);
            setMeshUris([]);
            setShowDrawingTools(false);
            setPendingGardenModification(null);
            setPubDetailsLoading(false);
            return;
        }

        if (currentlyLoadingPubIdRef.current === item.google_place_id && pubDetailsLoading) {
            return;
        }

        currentlyLoadingPubIdRef.current = item.google_place_id;
        setPubDetailsLoading(true);
        setError(null);
        setShowDrawingTools(false);
        setPendingGardenModification(null);

        if (currentPubData?.google_place_id !== item.google_place_id) {
            setReloadGardenController(prev => prev + 1);
        }

        try {
            const placeDetailsResponse = await getPlaceDetails(item.google_place_id);

            if (!placeDetailsResponse.data?.pubs?.[0]) {
                throw new Error("Could not fetch pub details from Google.");
            }
            const googlePubData = placeDetailsResponse.data.pubs[0] as PubObject;

            const pubForDisplay: PubDataForModeration = {
                ...googlePubData,
                original_sub_areas_count: item.sub_areas.length,
                first_submitter_name_from_queue: item.first_garden_submitter_name,
                first_submitter_type_from_queue: item.first_garden_submitter_type,
                date_submitted: new Date(item.date_added).toLocaleDateString(),
            };

            setMeshUris([]);
            setCurrentPubData(pubForDisplay);

            if (googlePubData.latitude && googlePubData.longitude) {
                setIsFetchingMeshes(true);
                const meshData = await fetchMinimalMeshes(googlePubData.latitude, googlePubData.longitude);
                const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
                
                if (meshData?.mesh_uris && meshData?.session_id) {
                    const completeUrls = meshData.mesh_uris.map((uri: string) => {
                        const path = uri.startsWith('/') ? uri : `/${uri}`;
                        const sessionQuery = meshData.session_id.includes('session=') ? meshData.session_id : `session=${meshData.session_id}`;
                        return `https://tile.googleapis.com${path}?${sessionQuery}&key=${googleTilesApiKey}`;
                    });
                    setMeshUris(completeUrls);
                }
                setIsFetchingMeshes(false);
            }
        } catch (err: any) {
            setError(`Failed to load details: ${err.message}.`);
            setMeshUris([]);
            setIsFetchingMeshes(false);
        } finally {
            setPubDetailsLoading(false);
            if (currentlyLoadingPubIdRef.current === item.google_place_id) {
                currentlyLoadingPubIdRef.current = null;
            }
        }
    }, [currentPubData, pubDetailsLoading]);

    // Load details when current index changes
    useEffect(() => {
        if (isAuthorized === false || isAuthorized === null || queueLoading) return;

        if (unmoderatedPubs.length > 0 && currentIndex < unmoderatedPubs.length) {
            const pubToLoad = unmoderatedPubs[currentIndex];
            if (pubToLoad && (!currentPubData || currentPubData.google_place_id !== pubToLoad.google_place_id)) {
                loadPubDetails(pubToLoad);
            }
        } else if (currentIndex >= unmoderatedPubs.length && isAuthorized) {
            setActionMessage("All pubs reviewed. Reloading list...");
            setTimeout(async () => {
                try {
                    const response = await getUnmoderatedPubs(1, 10);
                    setUnmoderatedPubs(response.data.pubs || []);
                    if (response.data.pubs?.length > 0) {
                        setCurrentIndex(0);
                        setActionMessage(null);
                    } else {
                        setActionMessage("No more pubs to moderate.");
                        setCurrentPubData(null);
                    }
                } catch (err) {
                    setError("Failed to reload unmoderated pubs.");
                }
            }, 1500);
        }
    }, [currentIndex, unmoderatedPubs, isAuthorized, queueLoading, loadPubDetails, currentPubData]);

    const handleActionInProgress = pubDetailsLoading || isFetchingMeshes;

    const handleNext = useCallback((skip: number = 1) => {
        if (handleActionInProgress && !(pubDetailsLoading && !currentPubData)) return;
        setShowDrawingTools(false);
        setPendingGardenModification(null);
        setActionMessage(null);
        setError(null);
        if (currentIndex + skip < unmoderatedPubs.length) {
            setCurrentIndex(currentIndex + skip);
        } else {
            setCurrentIndex(unmoderatedPubs.length);
        }
    }, [handleActionInProgress, pubDetailsLoading, currentPubData, currentIndex, unmoderatedPubs.length]);

    const handlePrevious = () => {
        if (handleActionInProgress) return;
        setShowDrawingTools(false);
        setPendingGardenModification(null);
        setActionMessage(null);
        setError(null);
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        }
    };

    const performActionAndMoveNext = async (
        actionAsync: () => Promise<void>,
        successMessage: string,
        errorMessage: string
    ) => {
        if (!currentPubData || (pubDetailsLoading && currentPubData) || isFetchingMeshes) return;

        setPubDetailsLoading(true);
        let actionSuccessful = false;
        try {
            await actionAsync();
            setActionMessage(successMessage);
            actionSuccessful = true;
        } catch (err: any) {
            setError(`${errorMessage} Error: ${err.message || 'Unknown error'}`);
            actionSuccessful = false;
        } finally {
            setPubDetailsLoading(false);
            if (actionSuccessful) {
                setPendingGardenModification(null);
                setShowDrawingTools(false);
                handleNext();
            }
        }
    };

    const handlePolygonCompleteForModeration = async (
        ecefCoordinates: [number, number, number][],
        gardenName?: string,
        isUncertain?: boolean
    ) => {
        if (!currentPubData) return;

        const convertedCoordinates = ecefCoordinates.map(coord =>
            gardenCallbackECEFToLatLonElev(coord[0], coord[1], coord[2])
        );

        const avgElevation = convertedCoordinates.length > 0
            ? convertedCoordinates.reduce((sum, coord) => sum + coord[2], 0) / convertedCoordinates.length
            : 0;

        let nameOnly = gardenName;
        let existingGardenUid: string | undefined = undefined;
        if (gardenName && gardenName.includes('::')) {
            const parts = gardenName.split('::');
            nameOnly = parts[0];
            existingGardenUid = parts[1];
        }

        try {
            setPubDetailsLoading(true);
            setError('');
            setActionMessage(`Saving garden '${nameOnly || 'garden'}'...`);
            
            const response = await submitGardenToDatabase(
                currentPubData.google_place_id,
                convertedCoordinates,
                nameOnly || 'garden',
                avgElevation,
                isUncertain,
                existingGardenUid,
                profile?.display_name || user?.email?.split('@')[0] || 'Moderator'
            );

            if (response.status === 200 || response.status === 201) {
                setPendingGardenModification({
                    coordinates: convertedCoordinates,
                    gardenName: nameOnly,
                    avgElevation,
                    isUncertain,
                    existingGardenUid
                });
                
                setActionMessage(`Garden "${nameOnly}" saved! Click 'Accept' to approve this pub.`);
                
                setTimeout(() => {
                    setReloadGardenController(prev => prev + 1);
                }, 800);
            }
        } catch (error: any) {
            setError(`Failed to save garden: ${error.message || 'Unknown error'}`);
            setActionMessage('');
        } finally {
            setPubDetailsLoading(false);
        }
    };

    const handleGardenDelete = useCallback(async (gardenUid: string) => {
        if (!currentPubData || !gardenUid) {
            setError('Cannot delete garden: Missing pub or garden ID.');
            return;
        }

        setPubDetailsLoading(true);
        setError('');
        setActionMessage(`Deleting garden...`);

        try {
            const response = await deleteGarden(gardenUid);

            if (response.status === 200 || response.status === 204) {
                setActionMessage('Garden deleted successfully!');
                setPendingGardenModification(null);
                
                setTimeout(() => {
                    setReloadGardenController(prev => prev + 1);
                    setActionMessage('');
                }, 800);
            }
        } catch (error: any) {
            setError(`Failed to delete garden: ${error.message || 'Unknown error'}`);
            setActionMessage('');
        } finally {
            setPubDetailsLoading(false);
        }
    }, [currentPubData]);

    const handleToggleUncertainty = useCallback(async (garden: Garden, isUncertain: boolean) => {
        if (!currentPubData || !garden.uid) {
            setError('Cannot update garden uncertainty.');
            return;
        }

        setPubDetailsLoading(true);
        setError('');
        setActionMessage(`Updating garden uncertainty...`);

        try {
            const response = await updateGardenUncertainty(
                currentPubData.google_place_id,
                garden,
                isUncertain
            );

            if (response.status === 200) {
                setActionMessage(`Garden marked as ${isUncertain ? 'uncertain' : 'certain'}.`);
                
                setTimeout(() => {
                    setReloadGardenController(prev => prev + 1);
                    setActionMessage('');
                }, 800);
            }
        } catch (error: any) {
            setError(`Failed to update uncertainty: ${error.message || 'Unknown error'}`);
            setActionMessage('');
        } finally {
            setPubDetailsLoading(false);
        }
    }, [currentPubData]);

    const handleAccept = async () => {
        const pubName = currentPubData?.name || "Pub";
        await performActionAndMoveNext(
            async () => {
                if (!currentPubData) throw new Error("Current pub data is null");
                await approveGarden(currentPubData.google_place_id);
            },
            `${pubName} approved successfully.`,
            `Failed to approve ${pubName}.`
        );
    };

    const handleReject = async () => {
        if (!currentPubData) return;
        setConfirmRejectDialogOpen(false);
        const pubName = currentPubData.name;
        await performActionAndMoveNext(
            async () => {
                if (!currentPubData) throw new Error("Current pub data is null");
                const currentQueueItem = unmoderatedPubs[currentIndex];
                if (currentQueueItem?.sub_areas) {
                    for (const subArea of currentQueueItem.sub_areas) {
                        await deleteGarden(subArea.uid);
                    }
                }
                await approveGarden(currentPubData.google_place_id);
            },
            `Gardens for ${pubName} rejected and pub marked as reviewed.`,
            `Failed to reject gardens for ${pubName}.`
        );
    };

    const handleModify = () => {
        if (handleActionInProgress) return;
        setShowDrawingTools(true);
        setActionMessage("Modify the garden using the tools. Click 'Accept' when done.");
    };

    const memoizedFocalPoint = useMemo<[number, number, number] | undefined>(() => {
        if (!currentPubData?.latitude || !currentPubData?.longitude) return undefined;
        return latLonElevToECEF(currentPubData.latitude, currentPubData.longitude, 0);
    }, [currentPubData?.latitude, currentPubData?.longitude]);

    if (authLoading || isAuthorized === null) {
        return <Container sx={{ textAlign: 'center', mt: 5 }}><CircularProgress /></Container>;
    }

    if (isAuthorized === false) {
        return (
            <>
                <TopNav title="Moderation Panel" />
                <Container sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h5" color="error">{error || "Access Denied."}</Typography>
                </Container>
            </>
        );
    }

    if (queueLoading && unmoderatedPubs.length === 0) {
        return (
            <>
                <TopNav title="Moderation Panel" />
                <Container sx={{ textAlign: 'center', mt: 5 }}>
                    <CircularProgress />
                    <Typography>Loading moderation queue...</Typography>
                </Container>
            </>
        );
    }

    if (unmoderatedPubs.length === 0 && !queueLoading && isAuthorized === true && !error && !actionMessage) {
        return (
            <>
                <TopNav title="Moderation Panel" />
                <Container sx={{ textAlign: 'center', mt: 5 }}>
                    <Typography variant="h6">No pubs to moderate at the moment.</Typography>
                </Container>
                <Box sx={{ px: '5%', mt: 'auto' }}><Footer /></Box>
            </>
        );
    }

    return (
        <>
            <Fonts />
            <TopNav title="Moderation Panel" />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px - 32px)' }}>
                <Box sx={{ flexGrow: 1 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
                    {actionMessage && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>{actionMessage}</Alert>}

                    {(pubDetailsLoading && !currentPubData && unmoderatedPubs.length > 0 && isAuthorized === true) &&
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 1 }}>Loading pub details...</Typography>
                        </Box>
                    }

                    {currentPubData && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, backgroundColor: themeColors.paperYellow }}>
                            <Typography variant="h5" gutterBottom sx={{ fontFamily: "'Paytone One', sans-serif", color: themeColors.darkBackground }}>
                                Moderating: {currentPubData.name}
                                <Chip label={`${currentIndex + 1} / ${unmoderatedPubs.length}`} size="small" sx={{ ml: 2 }} />
                            </Typography>
                            <Typography variant="subtitle1" color="text.secondary">
                                Address: {currentPubData.address}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Google Place ID: {currentPubData.google_place_id}
                                <Link
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(currentPubData.address || currentPubData.name || '')}&query_place_id=${currentPubData.google_place_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ ml: 1 }}
                                >
                                    <IconButton size="small"><OpenInNewIcon fontSize="inherit" /></IconButton>
                                </Link>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Date Added to Queue: {currentPubData.date_submitted}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                                    Submitted By: {currentPubData.first_submitter_name_from_queue}
                                </Typography>
                                {currentPubData.first_submitter_type_from_queue === 'registered' ? (
                                    <Chip icon={<PersonIcon />} label="Registered User" size="small" color="success" variant="outlined" />
                                ) : currentPubData.first_submitter_type_from_queue?.startsWith('anonymous') ? (
                                    <Chip icon={<VpnKeyIcon />} label="Anonymous Submission" size="small" color="warning" variant="outlined" />
                                ) : (
                                    <Chip label="Unknown Submitter" size="small" variant="outlined" />
                                )}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Original Gardens in Submission: {currentPubData.original_sub_areas_count}
                            </Typography>

                            <Box sx={{ height: '60vh', width: '100%', mb: 3, border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
                                {(isFetchingMeshes || (pubDetailsLoading && currentPubData)) && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'absolute', top: 0, left: 0, width: '100%', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10 }}>
                                        <CircularProgress /> <Typography sx={{ ml: 1 }}>{isFetchingMeshes ? "Loading 3D Map..." : "Performing action..."}</Typography>
                                    </Box>
                                )}
                                {meshUris.length > 0 && memoizedFocalPoint && currentPubData.google_place_id && currentPubData.name && !pubDetailsLoading ? (
                                    <GardenDrawController
                                        key={`${currentPubData.google_place_id}-${reloadGardenController}`}
                                        meshUris={meshUris}
                                        focalPoint={memoizedFocalPoint}
                                        pubName={currentPubData.name}
                                        googlePlaceId={currentPubData.google_place_id}
                                        onPolygonComplete={handlePolygonCompleteForModeration}
                                        onGardenDelete={handleGardenDelete}
                                        onToggleUncertainty={handleToggleUncertainty}
                                        showInitialHelp={false}
                                        handleGardenNaming={true}
                                        height="100%"
                                        reload={reloadGardenController}
                                    />
                                ) : (
                                    !isFetchingMeshes && !pubDetailsLoading && <Typography sx={{ p: 2, textAlign: 'center', mt: '25%' }}>No 3D map data available or pub has no coordinates.</Typography>
                                )}
                            </Box>

                            <Grid container spacing={1} justifyContent="center">
                                <Grid item xs={6} sm={4} md={2}>
                                    <Button fullWidth variant="outlined" startIcon={<SkipPreviousIcon />} onClick={handlePrevious} disabled={currentIndex === 0 || handleActionInProgress}>
                                        Previous
                                    </Button>
                                </Grid>
                                <Grid item xs={6} sm={4} md={3}>
                                    <Button fullWidth variant="contained" color="primary" startIcon={<CheckCircleOutlineIcon />} onClick={handleAccept} disabled={handleActionInProgress || !!(actionMessage?.includes('approved')) || !!(actionMessage?.includes('rejected'))}>
                                        {pendingGardenModification ? "Accept" : "Accept As Is"}
                                    </Button>
                                </Grid>
                                <Grid item xs={6} sm={4} md={2}>
                                    <Button fullWidth variant="outlined" color="info" startIcon={<EditIcon />} onClick={handleModify} disabled={handleActionInProgress || showDrawingTools}>
                                        Modify
                                    </Button>
                                </Grid>
                                <Grid item xs={6} sm={6} md={3}>
                                    <Button fullWidth variant="outlined" color="secondary" startIcon={<CancelIcon />} onClick={() => setConfirmRejectDialogOpen(true)} disabled={handleActionInProgress || !!(actionMessage?.includes('approved')) || !!(actionMessage?.includes('rejected'))}>
                                        Reject
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Button fullWidth variant="outlined" startIcon={<SkipNextIcon />} onClick={() => handleNext()} disabled={(currentIndex >= unmoderatedPubs.length - 1 && !queueLoading && unmoderatedPubs.length > 0) || handleActionInProgress}>
                                        Skip
                                    </Button>
                                </Grid>
                            </Grid>
                            {showDrawingTools && !pendingGardenModification &&
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Use the drawing tools above to modify the garden(s). Click "Accept" to save your changes and approve.
                                </Alert>
                            }
                        </Paper>
                    )}
                    {unmoderatedPubs.length === 0 && !queueLoading && isAuthorized === true && actionMessage && (
                        <Typography variant="h6" sx={{ textAlign: 'center', mt: 3 }}>{actionMessage}</Typography>
                    )}
                </Box>

                <Dialog
                    open={confirmRejectDialogOpen}
                    onClose={() => setConfirmRejectDialogOpen(false)}
                >
                    <DialogTitle>Confirm Rejection</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to reject this submission for "{currentPubData?.name || 'this pub'}"?
                            This will delete all submitted gardens for this pub. This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmRejectDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleReject} color="error">Confirm Reject</Button>
                    </DialogActions>
                </Dialog>
                <Box sx={{ pt: 4 }}><Footer /></Box>
            </Container>
        </>
    );
};

export default ModerationPanel;